import { estimateTextWidth, fitFontSizeToWidth, fitTextX, fluidFont, textYForBaseline } from '../layout';
export function digitalGaugeStyle(theme) {
    var isDigital = theme.background === '#020617';
    return {
        panelFill: isDigital ? '#041018' : '#111827',
        panelStroke: theme.dialStroke,
        labelColor: theme.textMuted,
        valueColor: theme.text,
        unitColor: theme.accent,
        barTrack: theme.lampOff,
        barFill: theme.accent,
        segmentOn: theme.accent,
        segmentOff: '#1e293b',
    };
}
function formatCompactDigitalValue(value, unit, format, boxW, maxFont) {
    if (!unit)
        return format(value);
    var rounded = format(value);
    var full = unit === 'RPM' ? "".concat(rounded).concat(unit) : "".concat(rounded, " ").concat(unit);
    if (estimateTextWidth(full, maxFont) <= boxW)
        return full;
    if (unit === 'RPM' && value >= 1000) {
        var short = "".concat((value / 1000).toFixed(1), "k");
        if (estimateTextWidth(short, maxFont) <= boxW)
            return short;
        return rounded;
    }
    if (unit === 'km/h' || unit === 'mph' || unit === '°C' || unit === '°F') {
        return rounded;
    }
    return full;
}
/** LCD-style digital gauge — large readout, label, optional bar/segments. */
export function buildDigitalGauge(app, group, bounds, style, opts) {
    var _a, _b, _c;
    var w = bounds.innerWidth;
    var h = bounds.innerHeight;
    var compact = h < 72 || w < 96;
    var micro = compact && (h < 56 || w < 60);
    var format = (_a = opts.formatValue) !== null && _a !== void 0 ? _a : (function (v) { return String(Math.round(v)); });
    var pct = Math.min(1, Math.max(0, opts.value / Math.max(opts.max, 1)));
    var abbrev = { SPEED: 'SPD', TEMPERATURE: 'TMP', TEMP: 'TMP' };
    var labelText = (micro ? (_b = abbrev[opts.label.toUpperCase()]) !== null && _b !== void 0 ? _b : opts.label.slice(0, 3) : opts.label).toUpperCase();
    group.add(app.roundedRect({
        width: w,
        height: h,
        cornerRadius: Math.min(10, h * 0.12),
        fill: style.panelFill,
        stroke: style.panelStroke,
        strokeWidth: compact ? 1 : 1.5,
        listening: false,
    }));
    var labelY = micro ? h * 0.2 : compact ? h * 0.22 : h * 0.18;
    var valueY = micro ? h * 0.58 : compact ? h * 0.52 : h * 0.48;
    var unitY = compact ? h * 0.78 : h * 0.68;
    if (!micro) {
        var labelSize = fluidFont(9, bounds, 6, 10);
        group.add(app.text({
            text: labelText,
            x: fitTextX(labelText, labelSize, w),
            y: textYForBaseline(labelY, labelSize, 'middle'),
            fontSize: labelSize,
            fontWeight: '600',
            fill: style.labelColor,
            textAlign: 'left',
            listening: false,
        }));
    }
    var displayVal = format(opts.value);
    var valueMax = fluidFont(compact ? 20 : 28, bounds, micro ? 8 : 12, 36);
    if (compact && opts.unit) {
        displayVal = formatCompactDigitalValue(opts.value, opts.unit, format, w, valueMax);
    }
    var fitted = fitFontSizeToWidth(displayVal, w, valueMax, micro ? 7 : 9);
    var valueText = app.text({
        text: displayVal,
        x: fitted.x,
        y: textYForBaseline(valueY, fitted.fontSize, 'middle'),
        fontSize: fitted.fontSize,
        fontWeight: 'bold',
        fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace',
        fill: style.valueColor,
        textAlign: 'left',
        listening: false,
    });
    group.add(valueText);
    if (opts.unit && !compact) {
        var unitSize = fluidFont(10, bounds, 7, 12);
        group.add(app.text({
            text: opts.unit,
            x: fitTextX(opts.unit, unitSize, w),
            y: textYForBaseline(unitY, unitSize, 'middle'),
            fontSize: unitSize,
            fontWeight: '600',
            fill: style.unitColor,
            textAlign: 'left',
            listening: false,
        }));
    }
    var barFill;
    if (opts.showBar !== false && !compact) {
        var barH = Math.max(4, Math.round(h * 0.06));
        var barY = h - barH - Math.max(6, bounds.pad * 0.4);
        var barW = w - bounds.pad;
        var barX = bounds.pad * 0.5;
        group.add(app.roundedRect({
            x: barX,
            y: barY,
            width: barW,
            height: barH,
            fill: style.barTrack,
            cornerRadius: barH / 2,
            listening: false,
        }));
        barFill = app.roundedRect({
            x: barX,
            y: barY,
            width: barW * pct,
            height: barH,
            fill: style.barFill,
            cornerRadius: barH / 2,
            listening: false,
        });
        group.add(barFill);
    }
    var segments = [];
    if (opts.showSegments && !compact) {
        var count = (_c = opts.segmentCount) !== null && _c !== void 0 ? _c : 12;
        var segW = Math.max(3, (w - bounds.pad * 2 - (count - 1) * 2) / count);
        var segH = Math.max(4, h * 0.05);
        var segY = h - segH - Math.max(8, bounds.pad * 0.5);
        var lit = Math.round(pct * count);
        for (var i = 0; i < count; i++) {
            var seg = app.roundedRect({
                x: bounds.pad * 0.5 + i * (segW + 2),
                y: segY,
                width: segW,
                height: segH,
                fill: i < lit ? style.segmentOn : style.segmentOff,
                cornerRadius: 1,
                listening: false,
            });
            segments.push(seg);
            group.add(seg);
        }
    }
    return { valueText: valueText, barFill: barFill, segments: segments, segmentOn: style.segmentOn, segmentOff: style.segmentOff };
}
export function updateDigitalGauge(parts, value, max, format, barWidth, segmentCount) {
    var pct = Math.min(1, Math.max(0, value / Math.max(max, 1)));
    parts.valueText.text = format(value);
    if (parts.barFill) {
        parts.barFill.width = barWidth * pct;
    }
    if (parts.segments && segmentCount) {
        var lit_1 = Math.round(pct * segmentCount);
        parts.segments.forEach(function (seg, i) {
            seg.fill = i < lit_1 ? parts.segmentOn : parts.segmentOff;
        });
    }
}
